import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Button, Spinner, Tooltip, TextInput } from 'flowbite-react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../hooks/useAuth';
import { useDatabase } from '../../hooks/useDatabase';
import ChangesTable, { Column } from '../../components/changes/ChangesTable';
import * as sched from 'sched';
import { FiInfo } from 'react-icons/fi';
import { useWorkspace } from '../../context/WorkspaceContext';
import Avatar from '../../components/common/Avatar';
import { useAsync, UseAsyncReturn } from 'react-async-hook';

type BeforeOrAfter = {
  type: 'before' | 'after';
  cursor: number;
}

const PAGE_SIZE = 100;

const ChangesListPage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { token, client } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { safeData, database } = useDatabase();
  const { getUserProfile } = useWorkspace();

  const [userProfiles, setUserProfiles] = useState<Record<string, sched.UserProfile>>({});
  const [profilesLoadedWorkspaceId, setProfilesLoadedWorkspaceId] = useState<string | null>(null);

  const [currentPageCursor, setCurrentPageCursor] = useState<BeforeOrAfter | null>(null);

  // This type hint is not actually correct - it should be `sched.Paginated<sched.ListWorkspaceEventsResponse, number>`
  // but typescript is apparently not able to properly handle something that extends its own generic parameter
  const currentPageData: UseAsyncReturn<sched.ListWorkspaceEventsResponse | null> = useAsync(async () => {
    if (!workspaceId || !token || !currentPageCursor) return null;

    return sched.WrappedApiClient.list_workspace_events(client, token, workspaceId, {
      limit: PAGE_SIZE,
      before: currentPageCursor?.type === 'before' ? currentPageCursor.cursor : null,
      after: currentPageCursor?.type === 'after' ? currentPageCursor.cursor : null,
    }).then((resp: sched.ListWorkspaceEventsResponse) => {
      resp.events.sort((a, b) => b.event_id - a.event_id);
      return resp;
    });
  }, [workspaceId, token, currentPageCursor]);

  const currentPageEventIds = {
    min: currentPageData.result?.events?.[currentPageData.result.events.length - 1]?.event_id ?? null,
    max: currentPageData.result?.events?.[0]?.event_id ?? null,
  }

  const loadLastEventId = useCallback((safeData: sched.SafeData) => {
    if (!safeData) return;
    sched.DataManager.get_last_remote_event_id(safeData).then(lastEventId => {
      console.log('last remote event id', lastEventId);
      if (lastEventId && currentPageCursor == null) {
        setCurrentPageCursor({ type: 'before', cursor: lastEventId });
      }
    });
  }, [])

  // Load the first event page on component load
  useEffect(() => {
    loadLastEventId(safeData);
  }, [safeData, database]);

  const handleShowOlder = async () => {
    if (currentPageEventIds.min == null) return;
    // fetch strictly older than current minimum
    await setCurrentPageCursor({ type: 'before', cursor: Math.max(0, currentPageEventIds.min - 1) });
  };

  const handleShowNewer = async () => {
    if (currentPageEventIds.max == null) return;
    await setCurrentPageCursor({ type: 'after', cursor: currentPageEventIds.max });
  };

  // Simple virtualized list implementation without extra deps
  const ITEM_HEIGHT = 56;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(200);
  const [footerHeight, setFooterHeight] = useState(0);
  const [search, setSearch] = useState('');

  const onScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setScrollTop(el.scrollTop);
  }, []);

  useEffect(() => {
    const computeHeight = () => {
      const headerEl = headerRef.current;
      const top = headerEl ? headerEl.getBoundingClientRect().bottom : (containerRef.current ? containerRef.current.getBoundingClientRect().top : 0);
      const footerEl = footerRef.current;
      const footerH = footerEl ? footerEl.getBoundingClientRect().height : 0;
      const padding = 8; // minimal bottom padding
      const available = Math.max(200, window.innerHeight - top - footerH - padding);
      setViewportHeight(available);
      setFooterHeight(footerH);
    };
    computeHeight();
    window.addEventListener('resize', computeHeight);

    // Observe header/footer size changes (e.g., when text wraps)
    const headerEl = headerRef.current;
    const footerEl = footerRef.current;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => computeHeight()) : null;
    if (ro) {
      if (headerEl) ro.observe(headerEl);
      if (footerEl) ro.observe(footerEl);
    }

    return () => {
      window.removeEventListener('resize', computeHeight);
      if (ro) ro.disconnect();
    };
  }, [currentPageData.result?.events.length]);

  // Fetch all workspace user profiles once per workspace and cache
  useEffect(() => {
    const run = async () => {
      try {
        if (!client || !token || !workspaceId) return;
        // Load once per workspace ID
        if (profilesLoadedWorkspaceId === workspaceId) return;
        const resp = await sched.WrappedApiClient.list_workspace_user_profiles(client, token, workspaceId);
        const map: Record<string, any> = {};
        for (const p of resp.user_profiles ?? []) {
          map[p.id] = p;
        }
        setUserProfiles(map);
        setProfilesLoadedWorkspaceId(workspaceId);
      } catch (e) {
        // ignore; we still fall back to raw IDs if names not found
      }
    };
    run();
  }, [client, token, workspaceId, profilesLoadedWorkspaceId]);

  // Backfill any missing creator profiles using WorkspaceContext.getUserProfile
  useEffect(() => {
    const run = async () => {
      if (!getUserProfile) return;
      const missing = Array.from(new Set(currentPageData.result?.events.map(e => e.creating_user_id))).filter(id => !userProfiles[id]);
      for (const id of missing) {
        try {
          const p = await getUserProfile(id);
          if (p) setUserProfiles(prev => ({ ...prev, [id]: p as any }));
        } catch (_) {
          // ignore
        }
      }
    };
    if (currentPageData.result?.events.length) run();
  }, [currentPageData.result?.events, getUserProfile, userProfiles]);

  const visibleEvents = React.useMemo(() => {
    const events = currentPageData.result?.events ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter(row => {
      const idStr = String(row.event_id);
      if (idStr.toLowerCase().includes(q)) return true;
      try {
        const payload = JSON.stringify(row.data).toLowerCase();
        return payload.includes(q);
      } catch {
        return false;
      }
    });
  }, [currentPageData.result?.events, search]);

  if (currentPageData.loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="xl" />
      </div>
    );
  }

  if (currentPageData.error) {
    return (
      <div className="text-center p-8 text-cm-text-muted">{currentPageData.error?.message}</div>
    );
  }


  const totalHeight = visibleEvents.length * ITEM_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - 2);
  const visibleCount = Math.ceil(viewportHeight / ITEM_HEIGHT) + 4;
  const endIndex = Math.min(visibleEvents.length, startIndex + visibleCount);
  const offsetY = startIndex * ITEM_HEIGHT;

  const disablePagination = currentPageData.loading || currentPageData.result?.events.length === 0;

  return (
    <div className="h-screen overflow-hidden p-4">
      <Card className="h-full">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold text-cm-text-primary">Workspace Changes</h2>
        </div>
        <div className="mb-3">
          <TextInput
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ID or payload..."
            sizing="md"
          />
        </div>
        <div className="mb-3">
          <Button className="w-full" color="light" size="sm" onClick={handleShowNewer} disabled={disablePagination}>
            Show newer events
          </Button>
        </div>
        {currentPageData.loading ? (
          <div className="flex justify-center items-center p-8">
            <Spinner size="xl" />
          </div>
        ) : currentPageData.result === null || currentPageData.result?.events.length === 0 ? (
          <div className="text-center p-8 text-cm-text-muted">No events found.<Button color="light" size="sm" onClick={() => loadLastEventId(safeData)}>Refresh</Button></div>
        ) : (
          <div className="w-full">
            {/* Header */}
            <div
              ref={headerRef}
              className="grid gap-4 text-xs uppercase text-cm-text-primary bg-cm-bg-elevated px-4 py-3 rounded-t-md [grid-template-columns:5rem_1.2fr_5rem_10rem]"
            >
              <div>Event ID</div>
              <div>Time</div>
              <div>User</div>
              <div>Preview</div>
            </div>
            {/* Virtualized body */}
            <div
              ref={containerRef}
              onScroll={onScroll}
              className="w-full overflow-auto"
              style={{ height: viewportHeight, minHeight: viewportHeight, maxHeight: viewportHeight, paddingBottom: Math.max(72, footerHeight + 24) }}
            >
              <div style={{ height: totalHeight, position: 'relative' }}>
                <div style={{ position: 'absolute', top: offsetY, left: 0, right: 0 }}>
                  {visibleEvents.slice(startIndex, endIndex).map((row) => {
                    const date = new Date(row.unix_timestamp_ms);
                    const timeLabel = isNaN(date.getTime()) ? String(row.unix_timestamp_ms) : date.toLocaleString();
                    const typeList = (row.data ?? []).map((ev: sched.DatabaseEvent) => {
                      if (!ev) return 'Unknown';
                      if (typeof ev === 'string') return ev;
                      if (typeof ev === 'object') {
                        const obj = ev as Record<string, unknown>;
                        // Prefer discriminator field
                        if (typeof (obj as any).type === 'string') return String((obj as any).type);
                        // Externally-tagged enum: { VariantName: { ... } }
                        const keys = Object.keys(obj);
                        if (keys.length >= 1) {
                          // Choose the first PascalCase-ish key if present
                          const pascal = keys.find(k => /[A-Z]/.test(k.charAt(0)));
                          return pascal ?? keys[0];
                        }
                      }
                      return 'Unknown';
                    });
                    const knownTypes = typeList.filter(t => t && t !== 'Unknown');
                    const types = knownTypes.length > 0 ? knownTypes.join(', ') : `${row.data?.length ?? 0} event${(row.data?.length ?? 0) === 1 ? '' : 's'}`;

                    const profile = userProfiles[row.creating_user_id];
                    const displayName = profile?.display_name ?? profile?.email ?? row.creating_user_id;
                    const avatarUrl = profile?.avatar_url;
                    return (
                      <div
                        key={row.event_id}
                        className="grid gap-4 items-center px-4 border-b border-cm-border-subtle [grid-template-columns:5.5rem_1.2fr_5rem_1.8fr]"
                        style={{ height: ITEM_HEIGHT }}
                      >
                        <div className="font-mono whitespace-nowrap overflow-hidden text-ellipsis">{row.event_id}</div>
                        <div className="whitespace-nowrap overflow-hidden text-ellipsis">{timeLabel}</div>
                        <div className="whitespace-nowrap overflow-hidden text-ellipsis">
                          <Tooltip className="truncate" content={displayName} ><Avatar name={displayName} imageUrl={avatarUrl} size="sm" /></Tooltip>
                        </div>
                        <div className="whitespace-nowrap overflow-hidden text-ellipsis flex items-center gap-2" title={types}>
                          <span className="truncate">{types}</span>
                          <Tooltip content={<pre className="max-w-[32rem] max-h-[28rem] overflow-auto whitespace-pre-wrap text-xs">{JSON.stringify(row, null, 2)}</pre>}>
                            <FiInfo className="shrink-0 text-cm-text-muted hover:text-cm-text-primary" />
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="sticky bottom-4 z-10 bg-cm-bg-surface/85/85 backdrop-blur supports-[backdrop-filter]:bg-cm-bg-surface/60 supports-[backdrop-filter]:bg-cm-bg-surface/60 pt-3 border-t border-cm-border-subtle shadow-sm rounded-b-lg" ref={footerRef}>
          <Button className="w-full" color="light" size="sm" onClick={handleShowOlder} disabled={disablePagination}>
            Show older events
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ChangesListPage;
